import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { validateEmail } from '../utils/helpers';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: contextUser, refreshUserData } = useUser();
  
  const locPay = searchParams.get('locPay'); // deposit, payment-link, etc.
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }

    // If user already has verified email, skip to next step
    if (contextUser.email && contextUser.email !== '') {
      handleRedirectAfterVerification();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser, navigate]);

  const handleRedirectAfterVerification = () => {
    if (locPay === 'deposit') {
      // Redirect to deposit page (account.html equivalent)
      navigate('/deposit');
    } else if (locPay === 'payment-link') {
      navigate('/payment-link');
    } else {
      // Default redirect to dashboard
      navigate('/dashboard');
    }
  };

  const handleEmailVerification = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const configBody = {
        username: contextUser.telegramName,
        email: email
      };

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configBody)
      };

      const response = await fetch(
        'https://tradebot-k4nt.onrender.com/api/payment/confirm-email',
        config
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Email verified successfully! 🎉');
        
        // Refresh user data to get updated email
        if (contextUser.pin) {
          await refreshUserData(contextUser.telegramName, contextUser.pin, true);
        }

        // Wait a moment then redirect
        setTimeout(() => {
          handleRedirectAfterVerification();
        }, 1500);
      } else {
        toast.error(data.error || data.message || 'Email verification failed');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!contextUser) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Mail className="text-blue-600" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Verify Your Email</h1>
            <p className="text-slate-600">
              {locPay === 'deposit' 
                ? 'Email verification is required to make deposits'
                : 'Please verify your email to continue'}
            </p>
          </div>
        </div>

        {/* Info Alert */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-600 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Why verify your email?</h3>
              <p className="text-blue-800 text-sm">
                Email verification helps us keep your account secure and allows you to receive important 
                notifications about your transactions and account activity.
              </p>
            </div>
          </div>
        </div>

        {/* Verification Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <form onSubmit={handleEmailVerification} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  required
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  Verify Email & Continue
                </>
              )}
            </button>
          </form>
        </div>

        {/* What Happens Next */}
        <div className="mt-6 bg-slate-50 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
          <ol className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <span className="font-semibold text-blue-600">1.</span>
              <span>We'll verify your email address with our system</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-blue-600">2.</span>
              <span>Your account will be updated with verified email status</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-blue-600">3.</span>
              <span>
                {locPay === 'deposit' 
                  ? "You'll be redirected to the deposit page to add funds"
                  : "You'll be redirected to continue your action"}
              </span>
            </li>
          </ol>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmailVerification;
